// FILECONTENT_START src/common/components/Calendar/Calendar.tsx
import { Box, BoxProps, styled } from "@mui/material";
import { clone } from "lodash";
import { Moment } from "moment";
import { FunctionComponent, MutableRefObject, ReactNode } from "react";

import {
  CalendarCellItem,
  CalendarDateRangeItem,
  CalendarEventItem,
  VIEWS,
} from "./calendarApi";
import CalendarCell, {
  CalendarCellProps,
  CALENDAR_MONTH_CELL_WIDTH,
} from "./CalendarCell";
import CalendarCellHeader, {
  CalendarCellHeaderProps,
} from "./CalendarCellHeader";
import CalendarHeaderCell, {
  CalendarHeaderCellProps,
} from "./CalendarHeaderCell";
import CalendarToolbar, { CalendarToolbarProps } from "./CalendarToolbar";
import useCalendar from "./useCalendar";

const HeaderContainer = styled(Box)`
  display: flex;
  justify-content: center;
  flex-direction: row;
  align-items: center;
`;

const Container = styled(Box)<BoxProps>`
  flex: 1;
  display: flex;
`;

const Content = styled(Box)`
  flex: 1;
  //background-color: red;
`;

interface BadgeProps extends BoxProps {
  color?: string;
}
const Badge = styled(Box, {
  shouldForwardProp: prop => prop !== "color",
})<BadgeProps>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  background-color: ${props => props.color || ""};
  border-radius: 5px;
  min-width: 20px;
  height: 20px;
  //align-self: center;
`;
const CellsContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

export interface CellContainerProps extends BoxProps {
  view?: VIEWS;
}

const CellContainer = styled(Box, {
  shouldForwardProp: prop => prop !== "view",
})<CellContainerProps>`
  //flex: ${props =>
    props.view === VIEWS.MONTH ? `1 1 ${CALENDAR_MONTH_CELL_WIDTH}` : "1"};
  display: flex;
  min-height: 50px;
  max-width: ${props => (props.view === VIEWS.WEEK ? "320px" : "")};
  min-width: ${props => (props.view === VIEWS.WEEK ? "320px" : "")};
  position: relative;
  //min-width: ${props => (props.view === VIEWS.DAY ? "100%" : "")};
`;

export interface CellBackgroundProps extends BoxProps {
  isHoliday?: boolean;
  isOtherMonth?: boolean;
  isCurrentDay?: boolean;
  isSelected?: boolean;
  view?: VIEWS;
}
const CellBackground = styled(Box, {
  shouldForwardProp: (prop: any) =>
    ![
      "isHoliday",
      "isOtherMonth",
      "isCurrentDay",
      "isSelected",
      "view",
    ].includes(prop),
})<CellBackgroundProps>`
  flex: 1;
  border-radius: 5px;
  margin: 1px;
`;

interface EventContainerProps extends BoxProps {
  color?: string;
}
const EventContainer = styled(Box, {
  shouldForwardProp: prop => prop !== "color",
})<EventContainerProps>`
  //flex: 1;
  border-radius: 5px;
  padding: 3px;
  margin: 1px;
  background-color: ${props => props.color || props.theme.palette.divider};
`;

export type CalendarRefType<
  EventType extends CalendarEventItem = CalendarEventItem
> = {
  getNextCell: (
    date?: string,
    hasEvent?: boolean
  ) => CalendarCellProps<EventType> | undefined;
  getPrevCell: (
    date?: string,
    hasEvent?: boolean
  ) => CalendarCellProps<EventType> | undefined;
};

export interface CalendarProps<EventType extends CalendarEventItem> {
  events?: EventType[];
  loadEvents?: (
    range: CalendarDateRangeItem
  ) => Promise<any> | undefined | void;
  onCellPress?: (cell: CalendarCellItem<EventType>, view: VIEWS) => void;
  date?: Moment;
  view?: VIEWS;
  views?: VIEWS[];
  /** Trigger cell press on specified views. Deafult trigger only MONTH view */
  triggerCellPressOnViews?: VIEWS[];
  buttons?: any[] | ReactNode;
  customToolbar?: any;
  loading?: boolean;
  showHeader?: boolean;
  selectedDate?: Moment;
  selectedRange?: CalendarDateRangeItem;
  containerStyle?: BoxProps["style"];
  swipeableStyle?: BoxProps["style"];
  toolbarStyle?: BoxProps["style"];
  navigateDelay?: number;
  ToolbarComponent?: FunctionComponent<CalendarToolbarProps>;
  HeaderCellComponent?: FunctionComponent<CalendarHeaderCellProps>;
  CellHeaderComponent?: FunctionComponent<CalendarCellHeaderProps>;
  CellComponent?: FunctionComponent<CalendarCellProps<EventType>>;
  showRefreshButton?: boolean;
  emptyCellPressEnabled?: boolean;
  sortFunction?: (a: any, b: any) => number;
  dayNameShortFunc?: (date: Moment) => string;
  calRef?: MutableRefObject<CalendarRefType<EventType> | undefined>;
}
export default function Calendar<EventType extends CalendarEventItem>({
  events,
  loadEvents,
  buttons,
  customToolbar,
  loading,
  onCellPress,
  showHeader = true,
  emptyCellPressEnabled = false,
  triggerCellPressOnViews = [VIEWS.MONTH],
  containerStyle,
  selectedRange,
  ToolbarComponent = CalendarToolbar,
  HeaderCellComponent = CalendarHeaderCell,
  CellComponent = CalendarCell,
  CellHeaderComponent = CalendarCellHeader,
  showRefreshButton,
  swipeableStyle,
  toolbarStyle,
  calRef,
  ...calProps
}: CalendarProps<EventType>) {
  const {
    view,
    //date,
    newDate,
    cal,
    newView,
    views,
    handleNavigate,
    handleView,
    refresh,
  } = useCalendar<EventType>({ events, loadEvents, ...calProps });

  //Ref
  if (calRef) {
    calRef.current = {
      getNextCell: (date, hasEvent = false) => {
        if (!date) {
          return;
        }
        let cell = cal.data?.find(
          d => date && d.date > date && (!hasEvent || d.events.length > 0)
        );
        if (!cell) {
          cell = cal.data?.find(d => !hasEvent || d.events.length > 0);
        }
        return cell;
      },
      getPrevCell: (date, hasEvent = false) => {
        if (!date) {
          return;
        }
        let cell = clone(cal.data)
          ?.reverse()
          .find(
            d => date && d.date < date && (!hasEvent || d.events.length > 0)
          );
        if (!cell) {
          cell = clone(cal.data)
            ?.reverse()
            .find(d => !hasEvent || d.events.length > 0);
        }
        return cell;
      },
    };
  }
  /*
  return (
    <div>
      <CalendarHeader
        label={cal.getLabel(newDate)}
        view={newView}
        views={views}
        onView={handleView}
        onNavigate={handleNavigate}
        buttons={buttons}
        customToolbar={customToolbar}
      />
      <div className={classes.grid} style={gridStyle}>
        {cal.headers.map((h, index) =>
          renderHeader
            ? renderHeader(h.title, index, h.date, view)
            : _renderHeader(h.title, index, h.date, view)
        )}
        {cal.data.map((d, index) => {
          const cellClass = classNames(classes.cell, {
            [classes.holiday]: d.isCurrentDay,
            [classes.otherMonth]: d.isOtherMonth && view === Views.MONTH
          });
          return (
            <div key={index} className={cellClass}>
              {renderCellHeader
                ? renderCellHeader(d, view)
                : _renderCellHeader(d, view)}
              {d.events.map(renderEvent || _renderEvent)}
            </div>
          );
        })}
        {loading && <LoadingIndicator opacity={0.5} />}
      </div>
    </div>
  );
*/
  return (
    <Container style={containerStyle}>
      {showHeader && (
        <ToolbarComponent
          label={cal.getLabel(newDate)}
          date={newDate}
          view={newView}
          views={views}
          onView={handleView}
          onNavigate={handleNavigate}
          buttons={buttons}
          customToolbar={customToolbar}
          loading={loading}
          refresh={refresh}
          showRefreshButton={showRefreshButton}
          containerStyle={toolbarStyle}
        />
      )}

      <Content
      //horizontal
      //scrollEnabled={view === VIEWS.WEEK}
      /*contentContainerStyle={{
            flex: view === VIEWS.WEEK ? 0 : 1,
            display: "flex",
            flexDirection: "column",
          }}*/
      >
        <HeaderContainer>
          {cal.headers.map((header, index) => (
            <CalendarHeaderCell key={index} {...header} view={view} />
          ))}
        </HeaderContainer>
        <CellsContainer>
          {cal.data?.map((d, index) => {
            const cellPressEnabled =
              triggerCellPressOnViews.includes(view) &&
              (d.events.length > 0 || emptyCellPressEnabled);
            return (
              <CellContainer
                key={index}
                view={view}
                style={{
                  width:
                    view === VIEWS.MONTH ? CALENDAR_MONTH_CELL_WIDTH : "100%",
                }}
                //activeOpacity={cellPressEnabled ? undefined : 1}
                onClick={
                  onCellPress && cellPressEnabled
                    ? () => onCellPress(d as CalendarCellItem<EventType>, view)
                    : undefined
                }
              >
                <CellBackground
                  isSelected={d.isSelected}
                  isHoliday={d.isHoliday}
                  isCurrentDay={d.isCurrentDay}
                  isOtherMonth={d.isOtherMonth && view === VIEWS.MONTH}
                >
                  <CellHeaderComponent {...d} view={view} />
                  <CellComponent
                    {...(d as CalendarCellItem<EventType>)}
                    view={view}
                    onPress={
                      onCellPress && cellPressEnabled
                        ? () =>
                            onCellPress(d as CalendarCellItem<EventType>, view)
                        : undefined
                    }
                  />
                </CellBackground>
              </CellContainer>
            );
          })}
        </CellsContainer>
        {/*loading && <LoadingIndicator opacity={0.5} />*/}
      </Content>
    </Container>
  );
}

// FILECONTENT_END src/common/components/Calendar/Calendar.tsx